import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Target, Users } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { JsonLd } from "@/components/JsonLd";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const SEOTomballPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  const canonicalUrl = generateCanonicalUrl('/seo-tomball');

  const content = {
    es: {
      title: "SEO Tomball TX | Posicionamiento Local en Google – Luis ROC",
      description: "Agencia SEO en Tomball, Texas. Optimización local para ZIP 77375, 77377. Mejora tu visibilidad en Google Maps y búsquedas locales. Reunión inicial gratuita.",
      h1: "SEO en Tomball TX — Posiciona tu negocio en Google",
      intro: "Tomball, ciudad histórica al noroeste de Houston con más de 12,000 residentes, es un mercado local competitivo. En Luis ROC implementamos estrategias SEO que posicionan tu negocio cuando vecinos de Tomball buscan tus servicios. Cobertura: ZIP 77375, 77377.",
      keywords: "seo tomball tx, agencia seo tomball texas, posicionamiento google tomball, seo local tomball"
    },
    en: {
      title: "SEO Tomball TX | Local Google Ranking – Luis ROC",
      description: "SEO agency in Tomball, Texas. Local optimization for ZIP 77375, 77377. Improve your visibility on Google Maps and local searches. Free 30-minute initial consultation.",
      h1: "SEO in Tomball TX — Rank your business on Google",
      intro: "Tomball, historic city northwest of Houston with over 12,000 residents, is a competitive local market. At Luis ROC we implement SEO strategies that position your business when Tomball neighbors search for your services. Coverage: ZIP 77375, 77377.",
      keywords: "seo tomball tx, seo agency tomball texas, google ranking tomball, local seo tomball"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": language === 'es' ? "SEO en Tomball TX" : "SEO in Tomball TX",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": {
      "@type": "City",
      "name": "Tomball",
      "containedIn": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": content[language].description
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://luisroc.com" },
      { "@type": "ListItem", "position": 2, "name": "Servicios", "item": "https://luisroc.com/servicios" },
      { "@type": "ListItem", "position": 3, "name": "SEO Tomball", "item": canonicalUrl }
    ]
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{content[language].title}</title>
        <meta name="description" content={content[language].description} />
        <meta name="keywords" content={content[language].keywords} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Hreflang tags */}
        
        {/* Geo tags */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Tomball" />
        <meta name="geo.position" content="30.0972;-95.6160" />
        
        {/* Open Graph */}
        <meta property="og:title" content={content[language].title} />
        <meta property="og:description" content={content[language].description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content={language === 'es' ? 'es_US' : 'en_US'} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content[language].title} />
        <meta name="twitter:description" content={content[language].description} />
      </Helmet>

      <JsonLd data={serviceSchema} />
      <JsonLd data={breadcrumbSchema} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNav 
            items={[
              { name: language === 'es' ? "Servicios" : "Services", url: "/servicios" },
              { name: language === 'es' ? "Localidades" : "Locations", url: "/servicios-locales-houston" },
              { name: "SEO Tomball", url: "/seo-tomball" }
            ]}
            language={language}
          />

          <section className="py-16 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              {content[language].h1}
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              {content[language].intro}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => window.location.href = '/diagnostico-seo-gratis'}>
                {language === 'es' ? 'Reunión inicial gratuita de 30 minutos' : 'Free 30-Minute Initial Consultation'}
              </Button>
              <Button size="lg" variant="outline" onClick={() => window.location.href = '/contacto'}>
                {language === 'es' ? 'Hablar con Experto' : 'Talk to Expert'}
              </Button>
            </div>
          </section>

          <section className="py-16 bg-accent/5 rounded-lg mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">
              {language === 'es' ? 'Estrategia SEO para Tomball' : 'SEO Strategy for Tomball'}
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Target className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'Segmentación hiperlocal' : 'Hyperlocal targeting'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es' 
                    ? 'Optimización específica para búsquedas "near me" y códigos postales de Tomball.'
                    : 'Specific optimization for "near me" searches and Tomball zip codes.'}
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Check className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'Google Business optimizado' : 'Optimized Google Business'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es'
                    ? 'Perfil completo, posts regulares, gestión de reseñas para máxima visibilidad.'
                    : 'Complete profile, regular posts, review management for maximum visibility.'}
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Users className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'Comunidad local' : 'Local community'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es'
                    ? 'Conexión con directorio local, eventos y asociaciones de Tomball.'
                    : 'Connection with local directory, events and Tomball associations.'}
                </p>
              </Card>
            </div>
          </section>

          <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              {language === 'es' ? 'Empieza a dominar Google hoy' : 'Start dominating Google today'}
            </h2>
            <p className="text-xl mb-6 opacity-90">
              {language === 'es' ? 'Auditoría SEO completa + estrategia personalizada' : 'Complete SEO audit + personalized strategy'}
            </p>
            <Button size="lg" variant="secondary" onClick={() => window.location.href = '/diagnostico-seo-gratis'}>
              {language === 'es' ? 'Solicitar Auditoría' : 'Request Audit'}
            </Button>
          </section>

          <section className="py-8 text-center">
            <h3 className="text-xl font-semibold mb-4">
              {language === 'es' ? 'Servicios en otras localidades' : 'Services in other locations'}
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="outline" onClick={() => window.location.href = '/seo-the-woodlands'}>SEO The Woodlands</Button>
              <Button variant="outline" onClick={() => window.location.href = '/seo-conroe'}>SEO Conroe</Button>
              <Button variant="outline" onClick={() => window.location.href = '/seo-magnolia'}>SEO Magnolia</Button>
              <Button variant="outline" onClick={() => window.location.href = '/servicios-locales-houston'}>
                {language === 'es' ? 'Ver Todas' : 'View All'}
              </Button>
            </div>
          </section>
        </main>

        <KnowledgeConnections path="/seo-tomball" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default SEOTomballPage;
